const ChemotherapyUnit3Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-blue-900 mb-3 border-b-2 border-slate-200 pb-2">Unit III: Chemotherapy (Part 2)</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            This unit continues the study of chemotherapy, focusing on highly specific infectious agents including Mycobacteria (TB, Leprosy), Fungi, Viruses, and Parasites (Malaria, Amoeba, Helminths).
        </p>
    </div>

    <!-- 2. Mycobacterial Infections (TB & Leprosy) -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">1. Anti-Mycobacterial Agents</h3>
        
        <!-- Antitubercular -->
        <div class="mb-6">
            <h4 class="text-[16px] font-bold text-blue-700 mb-2">A. Antitubercular Agents (For Tuberculosis)</h4>
            <p class="text-[13px] text-slate-700 mb-3">Tuberculosis (TB) is caused by <em>Mycobacterium tuberculosis</em>. The treatment requires multidrug therapy (DOTS) to prevent resistance.</p>
            
            <div class="overflow-x-auto rounded-lg border border-slate-200 mb-3">
                <table class="min-w-full bg-white text-[13px] text-left">
                    <thead class="bg-slate-100 text-slate-700">
                        <tr>
                            <th class="px-4 py-2 font-bold">First-Line Drug</th>
                            <th class="px-4 py-2 font-bold">Mechanism of Action (MOA)</th>
                            <th class="px-4 py-2 font-bold">Key Adverse Effects (ADR)</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 text-slate-600">
                        <tr>
                            <td class="px-4 py-3 font-bold text-blue-900">Isoniazid (INH)</td>
                            <td class="px-4 py-3">Inhibits the synthesis of <strong>Mycolic acid</strong>, an essential component of the mycobacterial cell wall. (Bactericidal)</td>
                            <td class="px-4 py-3 text-red-600">Peripheral neuropathy (prevented by giving Vitamin B6/Pyridoxine), Hepatotoxicity.</td>
                        </tr>
                        <tr>
                            <td class="px-4 py-3 font-bold text-blue-900">Rifampin (Rifampicin)</td>
                            <td class="px-4 py-3">Binds to the &beta;-subunit of bacterial <strong>DNA-dependent RNA polymerase</strong>, inhibiting RNA synthesis.</td>
                            <td class="px-4 py-3 text-red-600">Hepatotoxicity, <strong>Red-orange discoloration</strong> of urine, sweat, and tears. Strong CYP450 inducer.</td>
                        </tr>
                        <tr>
                            <td class="px-4 py-3 font-bold text-blue-900">Pyrazinamide</td>
                            <td class="px-4 py-3">Converted to active pyrazinoic acid in acidic environment of macrophages; exact MOA unclear. Highly effective against intracellular bacilli.</td>
                            <td class="px-4 py-3 text-red-600">Hyperuricemia (gout), highly hepatotoxic.</td>
                        </tr>
                        <tr>
                            <td class="px-4 py-3 font-bold text-blue-900">Ethambutol</td>
                            <td class="px-4 py-3">Inhibits <strong>Arabinosyl transferase</strong>, preventing the synthesis of arabinogalactan in the cell wall. (Bacteriostatic)</td>
                            <td class="px-4 py-3 text-red-600"><strong>Optic neuritis</strong> (Decreased visual acuity, red-green color blindness).</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Antileprotic -->
        <div>
            <h4 class="text-[16px] font-bold text-blue-700 mb-2">B. Antileprotic Agents (For Leprosy)</h4>
            <p class="text-[13px] text-slate-700 mb-2">Leprosy (Hansen's disease) is caused by <em>Mycobacterium leprae</em>. Treated with Multi-Drug Therapy (MDT).</p>
            <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-600">
                <li><strong>Dapsone:</strong> MOA: Structurally related to Sulfonamides; competitively inhibits Dihydropteroate synthase (inhibits folate synthesis). <span class="text-red-600">ADR: Hemolytic anemia (especially in G6PD deficiency).</span></li>
                <li><strong>Clofazimine:</strong> A phenazine dye. MOA: Binds to mycobacterial DNA and generates cytotoxic oxygen radicals. <span class="text-red-600">ADR: Reddish-black discoloration of skin and body fluids.</span></li>
                <li><strong>Rifampin:</strong> Highly bactericidal against M. leprae; same MOA as in TB.</li>
            </ul>
        </div>
    </div>

    <!-- 3. Fungal and Viral Infections -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        <!-- Antifungal -->
        <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <h3 class="text-[17px] font-black text-blue-800 mb-3">2. Antifungal Agents</h3>
            <p class="text-[12px] text-slate-700 mb-3">Fungi are eukaryotes. Their cell membranes contain <strong>Ergosterol</strong> instead of cholesterol.</p>
            <ul class="list-disc pl-5 space-y-3 text-[13px] text-slate-600">
                <li>
                    <strong>Polyenes (Amphotericin B, Nystatin):</strong><br>
                    <em>MOA:</em> Bind directly to ergosterol in the fungal cell membrane, forming artificial pores. Intracellular ions (K+) leak out, causing cell death.<br>
                    <span class="text-red-600 text-[11px] font-bold">ADR: Amphotericin B is highly Nephrotoxic ("Ampho-terrible").</span>
                </li>
                <li>
                    <strong>Azoles (Fluconazole, Itraconazole, Ketoconazole):</strong><br>
                    <em>MOA:</em> Inhibit the fungal CYP450 enzyme <strong>Lanosterol 14&alpha;-demethylase</strong>. This blocks the conversion of lanosterol to ergosterol.<br>
                    <span class="text-red-600 text-[11px] font-bold">ADR: Hepatotoxicity, inhibits human CYP450 enzymes causing drug interactions.</span>
                </li>
                <li>
                    <strong>Allylamines (Terbinafine):</strong><br>
                    <em>MOA:</em> Inhibits <strong>Squalene epoxidase</strong>, leading to a toxic accumulation of squalene and decreased ergosterol. Excellent for dermatophytosis (nail/skin infections).
                </li>
            </ul>
        </div>

        <!-- Antiviral -->
        <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <h3 class="text-[17px] font-black text-blue-800 mb-3">3. Antiviral Drugs</h3>
            <p class="text-[12px] text-slate-700 mb-3">Viruses are obligate intracellular parasites. Drugs target specific steps of viral replication.</p>
            <ul class="list-disc pl-5 space-y-3 text-[13px] text-slate-600">
                <li>
                    <strong>Anti-Herpes (Acyclovir):</strong><br>
                    <em>MOA:</em> It is a prodrug activated by viral thymidine kinase. It acts as a guanosine analog, competitively inhibiting viral <strong>DNA polymerase</strong> and causing DNA chain termination.
                </li>
                <li>
                    <strong>Anti-Retroviral / HIV Drugs:</strong>
                    <ul class="list-circle pl-4 mt-1 space-y-1 text-[12px]">
                        <li><em>NRTIs (Zidovudine):</em> Nucleoside analogs that inhibit Reverse Transcriptase.</li>
                        <li><em>NNRTIs (Nevirapine):</em> Non-nucleoside direct inhibitors of Reverse Transcriptase.</li>
                        <li><em>Protease Inhibitors (Ritonavir):</em> Inhibit viral protease, preventing the cleavage of polyproteins into mature functional viral proteins.</li>
                    </ul>
                </li>
                <li>
                    <strong>Anti-Influenza (Oseltamivir):</strong><br>
                    <em>MOA:</em> Inhibits viral <strong>Neuraminidase</strong>, preventing the release of new viral progeny from infected host cells.
                </li>
            </ul>
        </div>
    </div>

    <!-- 4. Parasitic Infections -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">4. Parasitic Infections</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
            <!-- Antimalarial -->
            <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                <h4 class="text-[15px] font-bold text-blue-700 mb-2">A. Antimalarial Drugs</h4>
                <p class="text-[12px] text-slate-600 mb-2">Target <em>Plasmodium</em> species.</p>
                <ul class="list-disc pl-4 space-y-1 text-[12px] text-slate-700">
                    <li><strong>Chloroquine:</strong> (Blood Schizonticide). MOA: Enters the parasite's food vacuole and inhibits Heme polymerase. Toxic heme accumulates and kills the parasite.</li>
                    <li><strong>Artemisinin (Artesunate):</strong> MOA: Cleavage of its endoperoxide bridge by iron (heme) generates highly reactive free radicals that damage parasite proteins. Fast acting.</li>
                    <li><strong>Primaquine:</strong> (Tissue Schizonticide). Kills dormant hypnozoites in the liver, providing a <em>radical cure</em> for P. vivax and P. ovale.</li>
                </ul>
            </div>

            <!-- Antiamoebic -->
            <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                <h4 class="text-[15px] font-bold text-blue-700 mb-2">B. Antiamoebic Agents</h4>
                <p class="text-[12px] text-slate-600 mb-2">Treat infections by <em>Entamoeba histolytica</em>.</p>
                <ul class="list-disc pl-4 space-y-1 text-[12px] text-slate-700">
                    <li><strong>Tissue Amoebicides (Metronidazole, Tinidazole):</strong> Target amoebae in the intestinal wall and liver. MOA: Its nitro group is reduced by anaerobic organisms to highly reactive cytotoxic intermediates that damage parasitic DNA. <span class="text-red-600 font-bold">ADR: Disulfiram-like reaction with alcohol.</span></li>
                    <li><strong>Luminal Amoebicides (Diloxanide furoate):</strong> Kills amoebic cysts in the intestinal lumen to prevent carrier state.</li>
                </ul>
            </div>

            <!-- Anthelmintics -->
            <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                <h4 class="text-[15px] font-bold text-blue-700 mb-2">C. Anthelmintics</h4>
                <p class="text-[12px] text-slate-600 mb-2">Drugs that expel parasitic worms (helminths).</p>
                <ul class="list-disc pl-4 space-y-1 text-[12px] text-slate-700">
                    <li><strong>Albendazole / Mebendazole:</strong> MOA: Bind to parasitic &beta;-tubulin and inhibit microtubule polymerization. This blocks glucose uptake, starving the worm.</li>
                    <li><strong>Ivermectin:</strong> MOA: Activates glutamate-gated chloride channels, causing hyperpolarization and flaccid paralysis of the worm.</li>
                    <li><strong>Praziquantel:</strong> Drug of choice for tapeworms & flukes. MOA: Increases cell membrane permeability to calcium, causing spastic paralysis of the worm.</li>
                </ul>
            </div>
        </div>
    </div>

</div>
`;

export default ChemotherapyUnit3Notes;